import React, { ChangeEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from '@/app/i18n/client'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/app/globalRedux/store'
import {
  setnewStreetAddress,
  setNewCompanyLogo,
  setNewCompanyName,
  setNewCountry,
  setNewPhoneCode,
  setNewPhoneNo,
  setNewProvince,
  setNewWebsite,
  setNewRegion,
  setNewCity,
  setSelectedCountry,
} from '@/app/globalRedux/myfructidor/companyProfileStore'
import { CountryType, ListType } from '@/app/types/constantDataType'
import { Autocomplete, Popper, TextField } from '@mui/material'
import {
  getCityList,
  getCountriesList,
  getProvinceList,
  getRegionsList,
} from '@/services/filterService'
import Image from 'next/image'
import { toast } from 'sonner'
import { ensureUrlHttpPrefix } from '@/lib/utils'
import { validatePhone } from '@/app/utils/validationUtils'

export const CustomPopper = function (props: any) {
  return (
    <Popper
      {...props}
      className={`py-2 custom-popper !w-[300px] z-20 !h-fit !absolute !left-[108px]`}
      placement="bottom"
    />
  )
}

const BasicInfo = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const {
    isloading,
    country_id,
    newCompanyLogo,
    newCompanyName,
    newWebsite,
    newPhoneNo,
    newStreetAddress,
    newCountry,
    newPhCode,
    newProvince,
    newRegion,
    newCity,
    newPhDialCode,
    selectedCountry,
  } = useSelector((state: RootState) => {
    return state.companyProfile
  })
  const [phoneCode, setPhoneCode] = useState<CountryType>({
    name: '',
    id: -1,
    dial_code: '',
    country_code: '',
    emoji: '',
    flag: '',
  })
  const [error, setError] = useState({
    company_name: '',
    streetAddress: '',
    phoneNumber: '',
  })
  const [logoUrl, setLogoUrl] = useState(newCompanyLogo)

  const countriesList = useSelector(
    (state: RootState) => state.constantData.countriesWithOutAllCountry
  )

  useEffect(() => {
    if (newPhCode && phoneCode?.name === '') {
      setPhoneCode({
        ...phoneCode,
        id: Number(newPhCode),
        dial_code: newPhDialCode,
      })
    }
  }, [newPhCode])

  useEffect(() => {
    setLogoUrl(newCompanyLogo)
  }, [newCompanyLogo])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type?.startsWith('image/')) {
        if (file.size <= 3 * 1000 * 1000) {
          const reader = new FileReader()
          reader.onload = () => {
            setLogoUrl(reader.result as string)
            dispatch(setNewCompanyLogo(file))
          }
          reader.readAsDataURL(file)
        } else {
          toast.warning(t('File size exceeds the limit of 3MB'))
          return
        }
      } else {
        toast.warning(t('Invalid file type.'))
      }
    }
  }
  const handleButtonClick = (): void => {
    const fileInput = document.getElementById(
      'company-logo'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.click()
    }
  }
  const handleCompanyNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const name = event.target.value
    dispatch(setNewCompanyName(name))
    if (name === '') {
      setError({ ...error, company_name: "Company Name Can't be Blank" })
    } else {
      setError({ ...error, company_name: '' })
    }
  }

  const handleWebsiteChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const site = event.target.value
    dispatch(setNewWebsite(site))
  }

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const phoneNumber = event.target.value.replace(/[^0-9]/g, '')
    dispatch(setNewPhoneNo(phoneNumber))
    if (!validatePhone(phoneNumber)) {
      setError({ ...error, phoneNumber: 'Please Enter Valid Phone Number' })
    } else {
      setError({ ...error, phoneNumber: '' })
    }
  }

  const handleStreetAddressChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const streetAddress = event.target.value
    dispatch(setnewStreetAddress(streetAddress))
    if (streetAddress === '') {
      setError({ ...error, streetAddress: "Address Can't be Blank" })
    } else {
      setError({ ...error, streetAddress: '' })
    }
  }

  const [countryList, setCountryList] = useState<CountryType[]>([])
  const [countryNameSearchText, setCountryNameSearchText] = useState<string>(
    newCountry || ''
  )
  const getCountryList = async () => {
    if (newCountry != '' && countryNameSearchText) {
      const response = await getCountriesList(countryNameSearchText)
      if (response !== undefined) {
        setCountryList(response)
      }
    }
  }
  useEffect(() => {
    if (countryNameSearchText?.length > 0) {
      getCountryList()
    }
  }, [countryNameSearchText])

  const [provinceList, setProvinceList] = useState<ListType[]>([])
  const [provinceNameSearchText, setProvinceNameSearch] = useState<string>(
    newProvince?.name || ''
  )
  const getProvinceStateList = async () => {
    if (newCountry != '' && provinceNameSearchText) {
      const response = await getProvinceList(
        provinceNameSearchText,
        newCountry,
        newRegion?.id
      )
      if (response !== undefined) {
        setProvinceList(response)
      }
    }
  }
  useEffect(() => {
    if (provinceNameSearchText?.length > 0) {
      getProvinceStateList()
    }
  }, [provinceNameSearchText])

  const [regionsList, setRegionsList] = useState<ListType[]>([])
  const [regionNameSearchText, setRegionNameSearch] = useState<string>('')
  const getRegionList = async () => {
    if (newCountry != '' && regionNameSearchText) {
      const response = await getRegionsList(newCountry, regionNameSearchText)
      if (response !== undefined) {
        setRegionsList(response)
      }
    }
  }
  useEffect(() => {
    if (regionNameSearchText?.length > 0) {
      getRegionList()
    }
  }, [regionNameSearchText])

  const [cityList, setCityList] = useState<ListType[]>([])
  const [cityNameSearchText, setCityNameSearch] = useState<string>('')
  const getCitiesList = async () => {
    if (newCountry != '' && cityNameSearchText) {
      const response = await getCityList(
        cityNameSearchText,
        newCountry,
        newProvince?.id,
        newRegion?.id
      )
      if (response !== undefined) {
        setCityList(response)
      }
    }
  }

  useEffect(() => {
    if (cityNameSearchText?.length > 0) {
      getCitiesList()
    }
  }, [cityNameSearchText])

  useEffect(() => {
    selectedCountry?.has_regions !== true && setNewRegion({ id: -1, name: '' })
    if (
      !isloading &&
      country_id !== '' &&
      selectedCountry?.id !== Number(country_id)
    ) {
      dispatch(setNewRegion({ id: '', name: '' }))
      dispatch(setNewProvince({ id: -1, name: '' }))
      dispatch(setNewCity({ id: '', name: '' }))
      setProvinceList([])
      setRegionsList([])
    }
  }, [selectedCountry])

  return (
    <div className="border border-gray_border rounded divide-y">
      <h2 className="bg-light_gray px-4 py-2 font-ns_bold text-lg">
        {t('Basic Information')}
      </h2>
      <div className="flex flex-col py-7 gap-y-9 p-4 text-[14px]">
        <div className="grid desktop:grid-cols-2 justify-between gap-8 mobile:grid-cols-1">
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="company_logo">{t('Logo')} </Label>
            <div className="flex items-center gap-6">
              {logoUrl ? (
                <label
                  htmlFor="company-logo"
                  className="w-[60px] h-[60px] border border-dashed rounded-full bg-contain bg-center bg-no-repeat"
                  style={{
                    backgroundImage: logoUrl ? `url(${logoUrl})` : 'none',
                  }}
                ></label>
              ) : (
                ''
              )}
              <div className="flex flex-col gap-2">
                <div className="">
                  <Button
                    variant="secondary"
                    onClick={handleButtonClick}
                    className="text-light_black font-ns_bold bg-pale_gold !border-pale_gold"
                    size="icon"
                  >
                    <Image
                      loading="lazy"
                      width={16}
                      height={15}
                      src="/assets/Icons/uploadIcon.svg"
                      alt="upload-icon"
                    />
                    {t('Upload Logo')}
                  </Button>
                </div>
                <p className="text-[10px] text-gray_text">
                  {t('At least 130 x 130px PNG, JPG, JPEG, WEBP file')}
                </p>
              </div>
              <input
                type="file"
                id="company-logo"
                className="hidden"
                accept="image/png, image/jpeg, image/webp, image/jpg"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
        <div className="grid desktop:grid-cols-2 justify-between gap-8 mobile:grid-cols-1">
          <div className="grid w-full items-center gap-3 relative">
            <Label htmlFor="company_name" className="required">
              {t('Company Name')}
            </Label>
            <Input
              id="company_name"
              placeholder={t('Enter Company')}
              type="text"
              value={newCompanyName}
              onChange={handleCompanyNameChange}
            />
            <p className="absolute -bottom-[24px] text-required_color">
              {t(error.company_name)}
            </p>
          </div>
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="country" className="required">
              {t('Country')}
            </Label>
            <Autocomplete
              popupIcon={
                <ExpandMoreIcon
                  className="text-expand_icon"
                  style={{ width: '20px', height: '20px' }}
                />
              }
              disableClearable
              disablePortal
              value={selectedCountry}
              options={countryList}
              getOptionLabel={(option) => `${option.emoji}  ${option.name}`}
              noOptionsText={
                countryNameSearchText === ''
                  ? t('Type to show Country')
                  : t('No Country Available')
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={`${option.name} ${option.dial_code}`}>
                    <h4>{`${option.emoji}  ${option.name}`}</h4>
                  </li>
                )
              }}
              inputValue={countryNameSearchText}
              onInputChange={(event, value) => {
                setCountryNameSearchText(value)
              }}
              id="country"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={t('Select Country')}
                />
              )}
              onChange={(event, value) => {
                if (value == null) {
                  dispatch(
                    setSelectedCountry({
                      name: '',
                      id: -1,
                      dial_code: '',
                      country_code: '',
                      emoji: '',
                      flag: '',
                      has_regions: false,
                    })
                  )
                  dispatch(setNewCountry(''))
                } else {
                  dispatch(setSelectedCountry(value))
                  dispatch(setNewCountry(String(value?.id)))
                }
              }}
            />
          </div>
        </div>
        <div className="grid desktop:grid-cols-2 justify-between gap-8 mobile:grid-cols-1">
          <div className="grid w-full items-center gap-3 relative">
            <Label
              htmlFor="region"
              className={`${selectedCountry?.has_regions === true ? '' : 'text-placeholder cursor-not-allowed'}`}
            >
              {t('Region')}
            </Label>
            <Autocomplete
              id="region"
              popupIcon={
                <ExpandMoreIcon
                  className="text-expand_icon"
                  style={{ width: '20px', height: '20px' }}
                />
              }
              disablePortal
              value={newRegion}
              options={regionsList}
              getOptionLabel={(option) => `${option.name}`}
              sx={{
                ' .Mui-disabled': {
                  cursor: 'not-allowed',
                },
              }}
              disabled={selectedCountry?.has_regions === true ? false : true}
              noOptionsText={
                regionNameSearchText === ''
                  ? t('Type to show Regions')
                  : t('Region Not Available')
              }
              inputValue={regionNameSearchText}
              onInputChange={(event, value) => {
                setRegionNameSearch(value)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('Select Region')}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              onChange={(event, value) => {
                if (value == null) {
                  dispatch(setNewRegion({ id: -1, name: '' }))
                } else {
                  dispatch(setNewRegion(value))
                }
              }}
            />
          </div>
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="province">{t('Province / State')}</Label>
            <Autocomplete
              popupIcon={
                <ExpandMoreIcon
                  className="text-expand_icon"
                  style={{ width: '20px', height: '20px' }}
                />
              }
              disablePortal
              value={newProvince?.name || ''}
              options={
                provinceList?.length >= 1
                  ? provinceList.map((item) => item.name)
                  : []
              }
              noOptionsText={
                provinceNameSearchText === ''
                  ? t('Type to show Provinces')
                  : t('No Province Available')
              }
              inputValue={provinceNameSearchText}
              onInputChange={(event, value) => {
                setProvinceNameSearch(value)
              }}
              id="province"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('Select Province / State')}
                />
              )}
              onChange={(event, value) => {
                if (value == null) {
                  dispatch(setNewProvince({ id: -1, name: '' }))
                }
                provinceList.forEach((v) => {
                  if (v.name == String(value)) {
                    dispatch(
                      setNewProvince({
                        id: v.id,
                        name: v.name,
                      })
                    )
                  }
                })
              }}
            />
          </div>
        </div>
        <div className="grid desktop:grid-cols-2 justify-between gap-8 mobile:grid-cols-1">
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="city">{t('City')}</Label>
            <Autocomplete
              freeSolo
              popupIcon={
                <ExpandMoreIcon
                  className="text-expand_icon"
                  style={{ width: '20px', height: '20px' }}
                />
              }
              disablePortal
              value={newCity?.name || ''}
              options={
                cityList?.length >= 1 ? cityList.map((item) => item.name) : []
              }
              noOptionsText={
                cityNameSearchText === ''
                  ? t('Type to show Cities')
                  : t('No Cities Available')
              }
              inputValue={cityNameSearchText}
              onInputChange={(event, value) => {
                setCityNameSearch(value)
                dispatch(setNewCity({ id: '', name: value }))
              }}
              id="city"
              renderInput={(params) => (
                <TextField {...params} placeholder={t('Select City')} />
              )}
              onChange={(event, value) => {
                if (value == null) {
                  dispatch(setNewCity({ id: '', name: '' }))
                } else {
                  cityList.forEach((v) => {
                    if (v.name == String(value)) {
                      dispatch(
                        setNewCity({
                          id: v.id,
                          name: v.name,
                        })
                      )
                    }
                  })
                }
              }}
            />
          </div>
          <div className="grid w-full items-center gap-3 relative">
            <Label htmlFor="address" className="required">
              {t('Address')}
            </Label>
            <Input
              id="address"
              placeholder={t('Enter Address')}
              type="text"
              value={newStreetAddress}
              onChange={handleStreetAddressChange}
            />
            <p className="absolute -bottom-[24px] text-required_color">
              {t(error.streetAddress)}
            </p>
          </div>
        </div>
        <div className="grid desktop:grid-cols-2 justify-between gap-8 mobile:grid-cols-1">
          <div className="grid w-full items-center gap-3 relative">
            <Label htmlFor="phone" className="required">
              {t('Phone')}
            </Label>
            <div className="grid grid-cols-10">
              <div className="col-span-2">
                <Autocomplete
                  disableClearable
                  disablePortal
                  forcePopupIcon={false}
                  id="country-phone-code"
                  PopperComponent={CustomPopper}
                  options={countriesList}
                  getOptionLabel={(option) => `${option.dial_code}`}
                  filterOptions={(options, state) =>
                    options.filter(
                      (option) =>
                        option.name
                          ?.toLowerCase()
                          .includes(state.inputValue?.toLowerCase()) ||
                        (option?.dial_code &&
                          option.dial_code.includes(state.inputValue))
                    )
                  }
                  sx={{
                    ' .MuiOutlinedInput-notchedOutline': {
                      border: '2px solid #e0e0e0 !important',
                      borderTopRightRadius: '0px !important',
                      borderBottomRightRadius: '0px !important',
                    },
                    '.MuiAutocomplete-input': {
                      textAlign: 'center',
                    },
                  }}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={t('Country Code')}
                      />
                    )
                  }}
                  onChange={(event, value) => {
                    dispatch(setNewPhoneCode(value.id))
                    setPhoneCode(value)
                  }}
                  value={phoneCode}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={`${option.name} ${option.dial_code}`}>
                        <span className="text-[14px]">{`${option.dial_code} (${option.name})`}</span>
                      </li>
                    )
                  }}
                  noOptionsText={
                    newPhCode === ''
                      ? t('Type Country Phone code')
                      : t('Phone Code Not Available')
                  }
                />
              </div>
              <Input
                className="col-span-8 !border-l-0 rounded-l-none outline-none"
                id="phone"
                placeholder={t('Phone')}
                type="text"
                maxLength={15}
                value={newPhoneNo}
                onChange={handlePhoneChange}
              />
              <p className="absolute -bottom-[24px] text-required_color">
                {t(error.phoneNumber)}
              </p>
            </div>
          </div>
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="website">{t('Website URL')} </Label>
            <Input
              id="website"
              placeholder={t('Enter Company Website')}
              type="text"
              value={newWebsite}
              onChange={handleWebsiteChange}
              onBlur={(e) => {
                const prefixedUrl = ensureUrlHttpPrefix(e?.target?.value)
                dispatch(setNewWebsite(prefixedUrl))
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
export default BasicInfo
